// components/DocumentViewerModal.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  CircularProgress,
  Typography,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import PropTypes from "prop-types";
import { GetApp } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      maxWidth: "90vw",
      maxHeight: "90vh",
      width: "800px",
      height: "600px",
    },
  },
  dialogFullscreen: {
    "& .MuiDialog-paper": {
      maxWidth: "100vw",
      maxHeight: "100vh",
      width: "100vw",
      height: "100vh",
      margin: 0,
      borderRadius: 0,
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    flex: 1,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    padding: theme.spacing(2),
  },
  titleContent: {
    display: "flex",
    flexDirection: "column",
  },
  documentInfo: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const DocumentViewerModal = ({
  open,
  onClose,
  documentUrl,
  documentName,
  documentType,
  isLoading,
  error,
}) => {
  const classes = useStyles();
  const [fullscreen, setFullscreen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  useEffect(() => {
    if (open) {
      setIframeLoading(true);
      setIframeError(false);
    }
  }, [open, documentUrl]);

  const handleFullscreenToggle = () => {
    setFullscreen(!fullscreen);
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleIframeError = () => {
    setIframeLoading(false);
    setIframeError(true);
  };

  const formatDocumentType = (type) =>
    type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const isImageFile = (url) =>
    /\.(jpg|jpeg|png|gif|bmp|webp|jfif)(\?.*)?$/i.test(url);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading document...
          </Typography>
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Document
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center">
            {error}
          </Typography>
        </div>
      );
    }

    if (!documentUrl && !isLoading && shouldRenderContent) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6" color="error" gutterBottom>
            Document Not Available
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center">
            Unable to load document URL
          </Typography>
        </div>
      );
    }

    if (isImageFile(documentUrl)) {
      return (
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={documentUrl}
            alt={documentName}
            style={{ maxWidth: "100%", maxHeight: "100%", height:"100%" }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      );
    }

    const googleViewerUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
      documentUrl
    )}`;
    return (
      <div style={{ height: "100%", position: "relative" }}>
        {(iframeLoading || iframeError) && (
          <div className={classes.loadingContainer}>
            {iframeLoading && (
              <>
                <CircularProgress />
                <Typography variant="body1" style={{ marginLeft: 16 }}>
                  Loading document...
                </Typography>
              </>
            )}
            {iframeError && (
              <>
                <Typography variant="h6" color="error" gutterBottom>
                  Error Loading Document
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  align="center"
                >
                  The document could not be displayed in the viewer.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginTop: 16 }}
                >
                  Open in New Tab
                </Button>
              </>
            )}
          </div>
        )}
        <iframe
          src={googleViewerUrl}
          className={classes.iframe}
          title={`${documentName} - ${formatDocumentType(documentType)}`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          style={{ display: iframeError || iframeLoading ? "none" : "block" }}
        />
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={fullscreen ? classes.dialogFullscreen : classes.dialog}
      maxWidth={false}
      onEnter={() => setShouldRenderContent(true)}
      onExit={() => setShouldRenderContent(false)}
      fullScreen={fullscreen}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div className={classes.titleContent}>
          <Typography variant="h6">
            {documentName || "Document Viewer"}
          </Typography>
          {documentType && (
            <Typography className={classes.documentInfo}>
              Type: {formatDocumentType(documentType)}
            </Typography>
          )}
        </div>
        <div className={classes.actionButtons}>
          {documentUrl && !error && (
            <IconButton
              href={documentUrl}
              rel="noopener noreferrer"
              title="Download"
              size="small"
            >
              <GetApp />
            </IconButton>
          )}

          <IconButton
            onClick={handleFullscreenToggle}
            title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}
            size="small"
          >
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton onClick={onClose} title="Close" size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

DocumentViewerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentUrl: PropTypes.string,
  documentName: PropTypes.string,
  documentType: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

DocumentViewerModal.defaultProps = {
  documentUrl: null,
  documentName: "",
  documentType: "",
  isLoading: false,
  error: null,
};

export default DocumentViewerModal;
